import { CheckCircle2, X, XCircle } from "lucide-react";
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";

/**
 * Short confirmations ("Settings saved") that don't move the page. Announced politely to
 * screen readers; they disappear after a few seconds and can be closed. Errors that need the
 * person to fix something stay inline next to the form instead.
 */

type Tone = "success" | "error";

interface Toast {
  id: number;
  tone: Tone;
  message: string;
}

interface Toaster {
  success: (message: string) => void;
  error: (message: string) => void;
}

const ToastContext = createContext<Toaster | null>(null);
const LIFETIME_MS = 5000;
const NOOP: Toaster = { success: () => undefined, error: () => undefined };

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => setToasts((current) => current.filter((t) => t.id !== id)), []);
  const show = useCallback(
    (tone: Tone, message: string) => {
      const id = nextId.current++;
      setToasts((current) => [...current.slice(-2), { id, tone, message }]);
      window.setTimeout(() => dismiss(id), LIFETIME_MS);
    },
    [dismiss],
  );
  const toaster = useMemo<Toaster>(
    () => ({ success: (m) => show("success", m), error: (m) => show("error", m) }),
    [show],
  );

  return (
    <ToastContext.Provider value={toaster}>
      {children}
      <div className="toast-region" role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast toast-${toast.tone}`}>
            {toast.tone === "success" ? (
              <CheckCircle2 size={18} className="toast-icon" aria-hidden="true" />
            ) : (
              <XCircle size={18} className="toast-icon" aria-hidden="true" />
            )}
            <span className="toast-text">{toast.message}</span>
            <button type="button" className="icon-btn" aria-label="Dismiss" onClick={() => dismiss(toast.id)}>
              <X size={16} aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/** Works outside the provider too (does nothing), so isolated components stay testable. */
export function useToast(): Toaster {
  return useContext(ToastContext) ?? NOOP;
}
